/** @format */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { aiService } from '../utils/aiService';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: any;
}

interface ChatContextType {
  isOpen: boolean;
  messages: Message[];
  isTyping: boolean;
  error: string | null;
  fullScreen: boolean;
  openChat: (options?: { fullScreen?: boolean; initialMessage?: string }) => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (content: string, type?: string, category?: string) => Promise<void>;
  clearHistory: () => void;
  clearError: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullScreen, setFullScreen] = useState(false);

  const openChat = useCallback((options?: { fullScreen?: boolean; initialMessage?: string }) => {
    if (options?.fullScreen !== undefined) setFullScreen(options.fullScreen);
    setIsOpen(true);
    
    if (options?.initialMessage) {
      const initialMsg: Message = {
        role: 'assistant',
        content: options.initialMessage,
        timestamp: new Date()
      };
      setMessages(prev => prev.length === 0 ? [initialMsg] : prev);
    }
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const sendMessage = useCallback(async (content: string, type?: string, category?: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setError(null);

    try {
      const result = await aiService.processChatMessage('global_user', content, { type, category });
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
        metadata: { confidence: result.confidence }
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error('Global Chat Error:', err);
      setError(err.message || 'Profstudymate is temporarily unavailable. Please try again later.');
    } finally {
      setIsTyping(false);
    }
  }, []);

  const clearHistory = useCallback(() => {
    setMessages([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        messages,
        isTyping,
        error,
        fullScreen,
        openChat,
        closeChat,
        toggleChat,
        sendMessage,
        clearHistory,
        clearError
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
